/**
 * App Theme Provider
 *
 * Fetches tenant configuration on app initialization and applies tenant-specific theming.
 * Two-phase loading:
 * 1. Fetch account branding (public API)
 * 2. If authenticated (token exists), fetch workspace branding (authenticated API)
 *
 * Shows loading spinner to prevent FOUC (Flash of Unstyled Content).
 */

import React, { ReactNode, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { authClient } from '../apis/client-instances';
import { useAppTheme } from '../contexts/ThemeContext';
import { setAccount, setError, setLoading } from '../features/account/accountSlice';
import { useWorkspaceBranding } from '../hooks/useWorkspaceBranding';
import { RootState } from '../types';
import { Box, CircularProgress } from './ui';

interface AppThemeProviderProps {
  children: ReactNode;
}

export const AppThemeProvider: React.FC<AppThemeProviderProps> = ({ children }) => {
  const { updateThemeColor } = useAppTheme();
  const dispatch = useDispatch();
  const { fetchAndApplyWorkspaceBranding } = useWorkspaceBranding();

  const isLoading = useSelector((state: RootState) => state.account.loading);
  const primaryColor = useSelector((state: RootState) => state.account.primaryColor);

  // Track when all branding (account + workspace) is complete
  const [brandingComplete, setBrandingComplete] = useState(false);

  useEffect(() => {
    const fetchBranding = async () => {
      dispatch(setLoading(true));

      try {
        // Phase 1: Fetch account branding (public API)
        const account = await authClient.getAccountByDomain();

        const accountData = {
          accountId: account.id,
          name: account.name,
          logoUrl: account.logoUrl,
          // Account-level favicon (when the tenant configured one).
          // Drives the collapsed-rail icon on Dentlogics-level pages.
          favicon: account.favicon || null,
          primaryColor: account.primaryColor,
          domain: account.domain,
          bannerHtml: account.bannerHtml || null,
        };

        dispatch(setAccount(accountData));

        // Update page title and favicon with account values initially
        if (account.name) {
          document.title = `${account.name} - LMS`;
          document.getElementById('favicon')?.setAttribute('href', account.favicon || '/favicon.ico');
        }

        // Phase 2: If token exists, fetch workspace branding
        const token = localStorage.getItem('token');
        if (token) {
          // Pass accountData directly to avoid stale Redux state
          await fetchAndApplyWorkspaceBranding(accountData);
        }
      } catch (error) {
        // Fail gracefully and use default theme
        console.warn('Failed to fetch tenant config, using defaults:', error);
        dispatch(setError('Failed to load account configuration'));
      } finally {
        dispatch(setLoading(false));
        setBrandingComplete(true);
      }
    };

    fetchBranding();
  }, [dispatch]);

  useEffect(() => {
    // Update theme with primary color when it's loaded
    if (primaryColor) {
      console.log('Applying tenant theme with color:', primaryColor);
      updateThemeColor(primaryColor);
    }
  }, [primaryColor]);

  // Show loading spinner until both account and workspace branding are complete
  if (isLoading || !brandingComplete) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          width: '100vw',
          backgroundColor: 'rgb(255, 247, 240)',
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  return <>{children}</>;
};
